import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  SlidersHorizontal,
  Info,
  AlertTriangle,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';

interface ConfigEntry {
  key: string;
  description: string | null;
  category: string;
  is_secret: boolean;
  updated_at: string;
}

interface UpsertConfigBody {
  key: string;
  value: string;
  description?: string;
  category: string;
  is_secret: boolean;
}

const CATEGORIES = ['api', 'auth', 'integrations', 'ai', 'general'] as const;

const CATEGORY_LABELS: Record<string, string> = {
  api: 'API',
  auth: 'Auth',
  integrations: 'Integrations',
  ai: 'AI',
  general: 'General',
};

const CATEGORY_COLORS: Record<string, string> = {
  api: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  auth: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  integrations: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ai: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  general: 'bg-muted text-muted-foreground',
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[category] ?? CATEGORY_COLORS.general}`}>
      {CATEGORY_LABELS[category] ?? category}
    </span>
  );
}

function ConfigSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-3 rounded-md border">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-5 w-16 rounded" />
          <Skeleton className="h-4 w-24 ml-auto" />
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      ))}
    </div>
  );
}

export default function AdminConfig() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState<ConfigEntry | null>(null);
  const [showValue, setShowValue] = useState(false);

  const [formKey, setFormKey] = useState('');
  const [formValue, setFormValue] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCategory, setFormCategory] = useState<string>('general');
  const [formIsSecret, setFormIsSecret] = useState(false);

  const { data: entries, isLoading, isError, refetch } = useQuery<ConfigEntry[]>({
    queryKey: ['/api/admin/config'],
  });

  const upsertMutation = useMutation({
    mutationFn: (body: UpsertConfigBody) =>
      apiRequest('POST', '/api/admin/config', body),
    onSuccess: () => {
      toast({ title: editEntry ? 'Config updated' : 'Config entry added', description: `Key "${formKey}" saved successfully.` });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/config'] });
      setDialogOpen(false);
    },
    onError: (err: any) => {
      toast({ title: 'Save failed', description: err?.message ?? 'Unknown error', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => apiRequest('DELETE', `/api/admin/config/${encodeURIComponent(key)}`),
    onSuccess: (_data, variables) => {
      toast({ title: 'Entry deleted', description: `Key "${variables}" removed.` });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/config'] });
      setDeleteKey(null);
    },
    onError: (err: any) => {
      toast({ title: 'Delete failed', description: err?.message ?? 'Unknown error', variant: 'destructive' });
    },
  });

  function openAdd() {
    setEditEntry(null);
    setFormKey('');
    setFormValue('');
    setFormDescription('');
    setFormCategory('general');
    setFormIsSecret(false);
    setShowValue(false);
    setDialogOpen(true);
  }

  function openEdit(entry: ConfigEntry) {
    setEditEntry(entry);
    setFormKey(entry.key);
    setFormValue('');
    setFormDescription(entry.description ?? '');
    setFormCategory(entry.category);
    setFormIsSecret(entry.is_secret);
    setShowValue(false);
    setDialogOpen(true);
  }

  function handleSubmit() {
    if (!formKey.trim()) {
      toast({ title: 'Key is required', variant: 'destructive' });
      return;
    }
    if (!formValue.trim()) {
      toast({ title: 'Value is required', variant: 'destructive' });
      return;
    }
    upsertMutation.mutate({
      key: formKey.trim(),
      value: formValue,
      description: formDescription || undefined,
      category: formCategory,
      is_secret: formIsSecret,
    });
  }

  const grouped = (entries ?? []).reduce<Record<string, ConfigEntry[]>>((acc, entry) => {
    const cat = entry.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(entry);
    return acc;
  }, {});

  const categoryOrder = ['api', 'auth', 'integrations', 'ai', 'general'];
  const sortedCategories = [
    ...categoryOrder.filter(c => grouped[c]?.length),
    ...Object.keys(grouped).filter(c => !categoryOrder.includes(c)),
  ];

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <SlidersHorizontal className="w-6 h-6 text-muted-foreground" />
              Configuration
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage runtime configuration values stored in the database. Changes take effect within 30 seconds — no restart needed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={openAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        </div>

        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
          <CardContent className="pt-4 pb-3">
            <div className="flex gap-2 text-sm text-amber-800 dark:text-amber-300">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Secret values are masked in this UI. To update a secret, open its edit dialog and enter the new value — the existing value cannot be retrieved.
              </span>
            </div>
          </CardContent>
        </Card>

        {isLoading && <ConfigSkeleton />}

        {isError && (
          <Card>
            <CardContent className="pt-6 text-center space-y-3">
              <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
              <p className="text-sm text-muted-foreground">Failed to load configuration entries.</p>
              <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && entries?.length === 0 && (
          <Card>
            <CardContent className="pt-10 pb-10 text-center space-y-4">
              <SlidersHorizontal className="w-10 h-10 text-muted-foreground mx-auto" />
              <div>
                <p className="font-medium">No configuration entries yet</p>
                <p className="text-sm text-muted-foreground mt-1">Add your first entry to get started.</p>
              </div>
              <Button onClick={openAdd}>
                <Plus className="w-4 h-4 mr-2" />
                Add your first entry
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && sortedCategories.map(category => (
          <div key={category} className="space-y-2">
            <div className="flex items-center gap-2">
              <CategoryBadge category={category} />
              <span className="text-xs text-muted-foreground">{grouped[category].length} {grouped[category].length === 1 ? 'entry' : 'entries'}</span>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {grouped[category].map(entry => (
                    <div key={entry.key} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {entry.is_secret && (
                          <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        )}
                        <code className="text-sm font-mono truncate">{entry.key}</code>
                      </div>
                      {entry.description && (
                        <span className="text-xs text-muted-foreground hidden md:block truncate max-w-xs">{entry.description}</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
                        {formatRelativeTime(entry.updated_at)}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(entry)}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteKey(entry.key)}
                        title="Delete"
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editEntry ? 'Edit Config Entry' : 'Add Config Entry'}</DialogTitle>
            <DialogDescription>
              {editEntry
                ? 'Update the value or metadata for this configuration key.'
                : 'Add a new key-value pair to the configuration table.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfg-key">Key</Label>
              <Input
                id="cfg-key"
                value={formKey}
                onChange={e => setFormKey(e.target.value)}
                placeholder="e.g. KUDOZ_API_URL"
                disabled={!!editEntry}
                className={editEntry ? 'opacity-60' : ''}
                spellCheck={false}
              />
              {editEntry && (
                <p className="text-xs text-muted-foreground">Key cannot be changed after creation.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cfg-value">Value</Label>
              <div className="relative">
                <Input
                  id="cfg-value"
                  type={formIsSecret && !showValue ? 'password' : 'text'}
                  value={formValue}
                  onChange={e => setFormValue(e.target.value)}
                  placeholder={editEntry && editEntry.is_secret ? 'Enter new value to update' : 'Enter value'}
                  spellCheck={false}
                  className={formIsSecret ? 'pr-10' : ''}
                />
                {formIsSecret && (
                  <button
                    type="button"
                    onClick={() => setShowValue(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {editEntry?.is_secret && (
                <p className="text-xs text-muted-foreground">
                  The existing value is not shown. Leave blank to keep the current value, or enter a new value to replace it.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cfg-description">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="cfg-description"
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="Brief description of this config value"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cfg-category">Category</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger id="cfg-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="cfg-secret"
                checked={formIsSecret}
                onCheckedChange={v => setFormIsSecret(!!v)}
              />
              <div>
                <Label htmlFor="cfg-secret" className="cursor-pointer">Mark as secret</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Value will be masked as *** in the UI.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={upsertMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? 'Saving…' : editEntry ? 'Save Changes' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteKey} onOpenChange={open => { if (!open) setDeleteKey(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete config entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <code className="font-mono bg-muted px-1 rounded">{deleteKey}</code> from the configuration table. The app will stop using this value on the next cache refresh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteKey && deleteMutation.mutate(deleteKey)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
